import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface MasqueradeBannerProps {
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    isMasquerading?: boolean;
    originalUserId?: string;
  };
}

export function MasqueradeBanner({ user }: MasqueradeBannerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const exitMasqueradeMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/admin/masquerade/exit");
    },
    onSuccess: () => {
      toast({
        title: "Exited Masquerade Mode",
        description: "You are now back to your admin account.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/cart"] });
      window.location.href = "/admin";
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to exit masquerade mode",
        variant: "destructive",
      });
    },
  });

  if (!user?.isMasquerading) {
    return null;
  }

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

  return (
    <div 
      className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between sticky top-0 z-[9999] shadow-md"
      data-testid="masquerade-banner"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5" />
        <span className="font-medium">
          Masquerade Mode: Viewing as{" "}
          <span className="font-bold">{displayName}</span>
          <span className="text-amber-800 ml-2">({user.email})</span>
        </span>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => exitMasqueradeMutation.mutate()}
        disabled={exitMasqueradeMutation.isPending}
        className="bg-white hover:bg-gray-100 text-black font-medium"
        data-testid="btn-exit-masquerade"
      >
        <LogOut className="h-4 w-4 mr-2" />
        {exitMasqueradeMutation.isPending ? "Exiting..." : "Exit Masquerade"}
      </Button>
    </div>
  );
}
